import { useState } from "react";
import { useRemoteContent } from "@/lib/ContentContext";
import { API_ENDPOINTS } from "@/lib/api";
import WeatherWidget from "./WeatherWidget";

interface ContactProps {
  lang: "zh" | "en";
}

export default function Contact({ lang }: ContactProps) {
  const [submitState, setSubmitState] = useState<"idle" | "loading" | "success" | "error">("idle");
  const siteContent = useRemoteContent();
  const { contact } = siteContent;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitState("loading");
    const form = e.currentTarget;
    const formData = new FormData(form);

    const name = formData.get("name")?.toString() ?? "";
    const phone = formData.get("phone")?.toString() ?? "";
    const email = formData.get("email")?.toString() ?? "";
    const type = formData.get("type")?.toString() ?? "";
    const message = formData.get("message")?.toString() ?? "";

    try {
      const res = await fetch(API_ENDPOINTS.contact, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, phone, email, type, message }),
      });

      if (res.ok) {
        setSubmitState("success");
        form.reset();
      } else {
        setSubmitState("error");
      }
    } catch {
      setSubmitState("error");
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-xh-bg">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/5 mb-4">
            <i className="ri-customer-service-2-line text-cyan-400 text-sm" />
            <span className="text-cyan-400 text-xs font-semibold tracking-widest uppercase">
              {lang === "zh" ? "联系我们" : "Contact Us"}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {lang === "zh" ? contact.zhTitle : contact.enTitle}
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto text-sm md:text-base">
            {lang === "zh" ? contact.zhSubtitle : contact.enSubtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Info panel */}
          <div className="flex flex-col gap-4">
            {/* Weather widget — full width prominent card */}
            <WeatherWidget lang={lang} />

            {/* Divider label */}
            <div className="flex items-center gap-3 px-1 mt-1">
              <span className="text-gray-600 text-xs tracking-widest uppercase">
                {lang === "zh" ? "联系方式" : "Contact Info"}
              </span>
              <div className="flex-1 h-px bg-white/5" />
            </div>

            {/* 2-column grid for contact info cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {contact.infoItems.map((item, i) => {
                const label = lang === "zh" ? item.zhLabel : item.enLabel;
                const value = lang === "zh" ? item.zhValue : item.enValue;
                return (
                  <div
                    key={i}
                    className="flex flex-col gap-3 p-4 rounded-xl bg-xh-bg-card border border-white/5 hover:border-cyan-500/30 transition-all duration-300"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-cyan-500/10 flex-shrink-0">
                      <i className={`${item.icon} text-cyan-400 text-base`} />
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs mb-1">{label}</p>
                      <p className="text-white text-sm font-medium leading-snug break-all">{value}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form */}
          <div className="bg-xh-bg-card rounded-2xl border border-white/5 p-6 md:p-8">
            {submitState === "success" ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[320px] text-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-cyan-500/15">
                  <i className="ri-check-line text-cyan-400 text-3xl" />
                </div>
                <h3 className="text-white text-lg font-bold">
                  {lang === "zh" ? "提交成功！" : "Submitted Successfully!"}
                </h3>
                <p className="text-gray-400 text-sm">
                  {lang === "zh"
                    ? "感谢您的留言，我们将尽快与您联系。"
                    : "Thank you! Our team will get back to you shortly."}
                </p>
                <button
                  onClick={() => setSubmitState("idle")}
                  className="mt-2 px-6 py-2.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-sm font-semibold hover:bg-cyan-500/20 transition-all cursor-pointer whitespace-nowrap"
                >
                  {lang === "zh" ? "再次提交" : "Submit Another"}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                <h3 className="text-white text-lg font-bold mb-6">
                  {lang === "zh" ? contact.zhFormTitle : contact.enFormTitle}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-400 text-xs mb-2">
                      {lang === "zh" ? "姓名 *" : "Full Name *"}
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder={lang === "zh" ? "请输入您的姓名" : "Your name"}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-500/50 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-2">
                      {lang === "zh" ? "电话" : "Phone"}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder={lang === "zh" ? "请输入联系电话" : "Your phone number"}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-500/50 transition-colors"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-gray-400 text-xs mb-2">
                    {lang === "zh" ? "邮箱 *" : "Email *"}
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder={lang === "zh" ? "请输入邮箱地址" : "your@email.com"}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-500/50 transition-colors"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-400 text-xs mb-2">
                    {lang === "zh" ? "合作意向" : "Inquiry Type"}
                  </label>
                  <select
                    name="type"
                    className="w-full bg-xh-bg-panel border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-500/50 transition-colors cursor-pointer"
                  >
                    <option value="">{lang === "zh" ? "请选择" : "Please select"}</option>
                    <option value="engine">{lang === "zh" ? "航空发动机研发" : "Aero Engine R&D"}</option>
                    <option value="uav">{lang === "zh" ? "无人机动力总成" : "UAV Propulsion"}</option>
                    <option value="parts">{lang === "zh" ? "精密零部件" : "Precision Parts"}</option>
                    <option value="service">{lang === "zh" ? "技术服务咨询" : "Technical Consulting"}</option>
                  </select>
                </div>
                <div className="mb-6">
                  <label className="block text-gray-400 text-xs mb-2">
                    {lang === "zh" ? "留言内容 *" : "Message *"}
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder={lang === "zh" ? "请描述您的需求或问题（500字以内）" : "Describe your needs (max 500 chars)"}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-500/50 transition-colors resize-none"
                  />
                </div>
                {submitState === "error" && (
                  <p className="text-red-400 text-xs mb-4">
                    {lang === "zh" ? "提交失败，请稍后重试。" : "Submission failed. Please try again."}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={submitState === "loading"}
                  className="w-full py-3 rounded-full bg-cyan-500 hover:bg-cyan-400 text-xh-bg-deep font-bold text-sm tracking-wide transition-all duration-300 hover:scale-[1.02] whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitState === "loading"
                    ? lang === "zh"
                      ? "提交中..."
                      : "Submitting..."
                    : lang === "zh"
                    ? "提交留言"
                    : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
